import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { EnhancedTableColumn, SortingState } from '../../types';
import { DraggableColumnHeader } from './draggable-column-header';

export interface EnhancedTableHeadProps<T extends Record<string, unknown>> {
  columns: EnhancedTableColumn<T>[];
  sorting?: SortingState<T> & {
    onRequestSort: (property: keyof T) => void;
  };
  onSelectAll?: (checked: boolean) => void;
  isAllSelected?: boolean;
  isIndeterminate?: boolean;
  showSelectAll?: boolean;
  isFullscreen?: boolean;
  columnResize?: boolean;
  onColumnResize?: (columnId: string, width: number) => void;
  columnWidths?: Record<string, number>;
  columnReorder?: boolean;
  tableLayout?: 'auto' | 'fixed';
}

function ResizableTableCell<T>({
  column,
  width,
  onColumnResize,
  children,
  ...props
}: {
  column: EnhancedTableColumn<T>;
  width: number;
  onColumnResize?: (columnId: string, width: number) => void;
  children: React.ReactNode;
} & React.ComponentProps<typeof TableCell>) {
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      setIsDragging(true);
      setStartX(event.clientX);
      setStartWidth(width);
    },
    [width],
  );

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isDragging) return;

      const deltaX = event.clientX - startX;
      const newWidth = Math.max(column.minWidth || 50, startWidth + deltaX);

      if (column.maxWidth) {
        const finalWidth = Math.min(newWidth, column.maxWidth);
        onColumnResize?.(String(column.id), finalWidth);
      } else {
        onColumnResize?.(String(column.id), newWidth);
      }
    },
    [
      isDragging,
      startX,
      startWidth,
      column.minWidth,
      column.maxWidth,
      column.id,
      onColumnResize,
    ],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
    return undefined;
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <TableCell
      {...props}
      sx={{
        position: 'relative',
        width: width ? `${width}px` : undefined,
        minWidth: column.minWidth ? `${column.minWidth}px` : undefined,
        maxWidth: column.maxWidth ? `${column.maxWidth}px` : undefined,
        overflow: 'visible', // Ensure resize handle is visible
        height: 56, // Explicit height to ensure consistency
        boxSizing: 'border-box',
        ...props.sx,
      }}
    >
      {children}
      {column.resizable && (
        <Box
          className={isDragging ? 'dragging' : ''}
          onMouseDown={handleMouseDown}
          sx={{
            position: 'absolute',
            right: 0, // Position exactly at the edge
            top: 0, // Full height alignment
            bottom: 0, // Full height alignment
            width: 4, // Clickable area width
            cursor: 'col-resize',
            backgroundColor: 'transparent',
            zIndex: 10,
            transition: 'background-color 0.2s ease',
            // Always visible vertical line
            '&::before': {
              content: '""',
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: '1px',
              backgroundColor: 'divider',
            },
            '&:hover': {
              backgroundColor: 'action.hover',
              '&::before': {
                backgroundColor: 'primary.main',
              },
            },
            '&.dragging': {
              backgroundColor: 'action.selected',
              '&::before': {
                backgroundColor: 'primary.main',
              },
            },
          }}
        />
      )}
    </TableCell>
  );
}

export function EnhancedTableHead<T extends Record<string, unknown>>({
  columns,
  sorting,
  onSelectAll,
  isAllSelected = false,
  isIndeterminate = false,
  showSelectAll = false,
  isFullscreen = false,
  columnResize = false,
  onColumnResize,
  columnWidths = {},
  columnReorder = false,
  tableLayout = 'auto',
}: EnhancedTableHeadProps<T>) {
  const handleSort = (property: keyof T) => {
    sorting?.onRequestSort(property);
  };

  return (
    <TableHead>
      <TableRow data-fullscreen={isFullscreen}>
        {showSelectAll && (
          <TableCell padding="checkbox" sx={{ height: 56 }}>
            <Checkbox
              indeterminate={isIndeterminate}
              checked={isAllSelected}
              onChange={(e) => onSelectAll?.(e.target.checked)}
            />
          </TableCell>
        )}
        {columns.map((column) => {
          // Use explicit width if set in columnWidths or column.width
          // Fallback behavior depends on tableLayout:
          // - tableLayout: 'fixed': undefined allows columns to share remaining space
          // - tableLayout: 'auto' (default): 120px fallback for backward compatibility
          const columnWidth =
            columnWidths[String(column.id)] ??
            column.width ??
            (tableLayout === 'fixed' ? undefined : 120);
          const isResizable = columnResize && column.resizable;
          const isDraggable = columnReorder;

          const cellContent =
            column.sortable && sorting ? (
              <TableSortLabel
                active={sorting.orderBy === column.id}
                direction={
                  sorting.orderBy === column.id ? sorting.order : 'asc'
                }
                onClick={() => handleSort(column.id as keyof T)}
              >
                {column.label}
              </TableSortLabel>
            ) : (
              column.label
            );

          // Handle both resizable and draggable columns
          if (isDraggable && isResizable) {
            return (
              <DraggableColumnHeader key={String(column.id)} column={column}>
                <ResizableTableCell
                  column={column}
                  width={columnWidth}
                  onColumnResize={onColumnResize}
                  align={column.align || 'left'}
                  sortDirection={
                    sorting?.orderBy === column.id ? sorting.order : false
                  }
                >
                  {cellContent}
                </ResizableTableCell>
              </DraggableColumnHeader>
            );
          }

          if (isDraggable) {
            return (
              <DraggableColumnHeader key={String(column.id)} column={column}>
                <TableCell
                  align={column.align || 'left'}
                  sx={{
                    minWidth: column.minWidth
                      ? `${column.minWidth}px`
                      : undefined,
                    width: columnWidth ? `${columnWidth}px` : undefined,
                    maxWidth: column.maxWidth
                      ? `${column.maxWidth}px`
                      : undefined,
                    height: 56, // Explicit height to ensure consistency
                    boxSizing: 'border-box',
                  }}
                  sortDirection={
                    sorting?.orderBy === column.id ? sorting.order : false
                  }
                >
                  {cellContent}
                </TableCell>
              </DraggableColumnHeader>
            );
          }

          if (isResizable) {
            return (
              <ResizableTableCell
                key={String(column.id)}
                column={column}
                width={columnWidth}
                onColumnResize={onColumnResize}
                align={column.align || 'left'}
                sortDirection={
                  sorting?.orderBy === column.id ? sorting.order : false
                }
              >
                {cellContent}
              </ResizableTableCell>
            );
          }

          // Regular non-resizable, non-draggable column
          return (
            <TableCell
              key={String(column.id)}
              align={column.align || 'left'}
              sx={{
                minWidth: column.minWidth ? `${column.minWidth}px` : undefined,
                width: columnWidth ? `${columnWidth}px` : undefined,
                maxWidth: column.maxWidth ? `${column.maxWidth}px` : undefined,
                height: 56, // Explicit height to ensure consistency
                boxSizing: 'border-box',
              }}
              sortDirection={
                sorting?.orderBy === column.id ? sorting.order : false
              }
            >
              {cellContent}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}
